"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// ---------- TOC sections (anchored to H2 ids in the article body) ----------

const SECTIONS = [
  { id: "what", t: "What is it?" },
  { id: "when", t: "When does it trigger?" },
  { id: "fix", t: "How to silence & fix" },
  { id: "prevent", t: "Prevention checklist" },
  { id: "brands", t: "By brand · codes" },
] as const

// ---------- Page ----------

export function TemperatureAlarmGuidePageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // IntersectionObserver-driven active TOC section
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Temperature Alarm Guide"
      heroEyebrow="Temperature"
      heroTitlePre="Why is your fridge"
      heroTitleItalic="beeping"
      heroTitlePost="at you?"
      heroSubtitle="A temperature alarm isn't a malfunction — it's your fridge flagging that something is drifting warm. Here's how to read it, silence it, and fix the cause."
      heroMeta={["⏱ ~6 min read", "USDA + manufacturer guidance"]}
      heroShare={<ToolShareBar title="Why is your fridge beeping at you?" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            Quick answer
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Most alarms trigger when the fridge stays above{" "}
            <em className="italic text-[#ff9951]">40°F</em> or the freezer above{" "}
            <em className="italic text-[#ff9951]">10°F</em> for 2+ hours.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Safe fridge
              </div>
              <div className="text-[22px] font-black tracking-tight">35–38°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Safe freezer
              </div>
              <div className="text-[22px] font-black tracking-tight">0–5°F</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Is my food still safe?",
          answer:
            "If it held at or below 40°F the whole time, yes. Once above 40°F for over 2 hours, discard meat, dairy, and leftovers per USDA guidance. Condiments and hard cheese are usually fine.",
        },
        {
          question: "My fridge beeps but the temp reads normal — why?",
          answer:
            "Classic thermistor fault. The sensor is reading wrong and tripping the alarm incorrectly. The part is ~$25 but placement is awkward — call service.",
        },
        {
          question: "How loud should the alarm be?",
          answer:
            "Audible from the next room but not across the house. If yours is silent, the speaker may be bad — check your user manual for the self-test sequence.",
        },
      ]}
      relatedTools={[
        { title: "Temperature Converter", category: "Temperature", description: "°F ↔ °C for every storage zone.", href: "/tools/cooking-temperature-converter", time: "~5 sec" },
        { title: "Shelf Life Calculator", category: "Food Storage", description: "How long before food actually goes bad.", href: "/tools/food-storage-calculator", time: "~15 sec" },
        { title: "Lifespan Estimator", category: "Specialty", description: "Years of life remaining on your fridge.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "Check if coils need cleaning.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
      ]}
      cta={{
        title: "Still beeping after all five steps?",
        body: "It's likely a failing gasket, thermistor, or compressor. Run our repair-vs-replace tool to see whether it's worth fixing or time for a new unit.",
        buttonLabel: "Check repair vs replace",
        buttonHref: "/tools/repair-or-replace-refrigerator",
      }}
    >
      {/* ============ ARTICLE BODY WITH STICKY TOC ============ */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* Article body */}
          <article>
            <GuideTool
              eyebrow="Interactive threshold setter"
              title="Set your alarm thresholds"
              intro="What you protect decides the numbers. Two questions, then the set points to program."
              questions={[
                            {
                              id: "contents",
                              label: "What are you protecting?",
                              options: [
                                { value: "groceries", label: "Everyday groceries" },
                                { value: "meds", label: "Medication / insulin" },
                                { value: "garage", label: "A garage freezer full of meat" }
                              ],
                            },
                            {
                              id: "unit",
                              label: "Which unit gets the alarm?",
                              options: [
                                { value: "fridge", label: "Refrigerator" },
                                { value: "freezer", label: "Freezer" },
                                { value: "both", label: "Both" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.contents === "meds") {
                              return { tone: "warn", verdict: "High 46\u00b0F \u00b7 Low 36\u00b0F \u00b7 log continuously", detail: "Medication holds the CDC band, 36\u201346\u00b0F \u2014 alarm at both edges, and use a digital data logger rather than a spot thermometer so an excursion during sleep or travel is recorded, not missed." }
                            }
                            const fridge = "Refrigerator: alarm high at 40\u00b0F (the USDA safety line) with the unit set at 37\u00b0F, and low at 33\u00b0F to catch freeze-ups."
                            const freezer = "Freezer: alarm high at 5\u00b0F with the unit set at 0\u00b0F \u2014 five degrees of headroom rides out defrost cycles without false alarms" + (a.contents === "garage" ? ", and for a meat-loaded garage unit add a door-ajar alert if the alarm offers one \u2014 that is the failure that actually empties a freezer." : ".")
                            const detail = a.unit === "fridge" ? fridge : a.unit === "freezer" ? freezer : fridge + " " + freezer
                            return { tone: "good", verdict: a.unit === "both" ? "40\u00b0F fridge \u00b7 5\u00b0F freezer" : a.unit === "fridge" ? "High 40\u00b0F \u00b7 Low 33\u00b0F" : "High 5\u00b0F", detail }
                          }}
              ctaLabel="Set my thresholds"
              footnote="Bands: USDA 40\u00b0F/0\u00b0F and the CDC 36\u201346\u00b0F medication range."
            />
            {/* What */}
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What is a temperature alarm?
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Modern refrigerators ship with a sealed thermistor in each compartment. When the
                reading drifts outside the safe band for longer than a factory-set grace window,
                the control board triggers an audible beep and a panel code.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                It&apos;s a <em className="font-semibold not-italic text-[#ff9951]">warning</em>, not an
                error — the alarm is correctly working. The cause, however, is almost always
                fixable in under ten minutes.
              </p>

              {/* Callout */}
              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Food safety clock starts at 40°F
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    USDA guidance: food held above 40°F for more than <strong>2 hours</strong>{" "}
                    should be discarded. Plan your fix accordingly.
                  </p>
                </div>
              </div>
            </section>

            {/* When */}
            <section id="when" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                When does it trigger?
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Five common triggers, ordered from most likely to least likely. Open
                doors are by far the most common cause; failing components are rare.
              </p>
              <div className="space-y-3">
                {[
                  { t: "Door left open", d: "Most common by far. The alarm silences instantly when you close the door firmly." },
                  { t: "Hot food placed inside", d: "A pot of soup or a tray of leftovers above 80°F raises the internal temp past the threshold." },
                  { t: "Condenser coils blocked", d: "Dust, pet hair, or cabinet obstruction prevents heat dissipation." },
                  { t: "Power interruption", d: "A brownout or outage reset the compressor. The alarm clears once temp recovers." },
                  { t: "Failing component", d: "Door gasket, thermistor, or compressor. Needs a technician." },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Fix — keep the numbered step list inside (numbering steps is fine) */}
            <section id="fix" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                How to silence &amp; fix it
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Silence the beep", d: "Most units: press the 'Alarm' or 'Reset' button on the panel once. Some brands (LG, Samsung) use a long-press of the freezer button." },
                  { n: "02", t: "Close everything firmly", d: "Press all doors and drawers shut. Watch for magnets to fully catch — partial closure is the #1 cause of repeat alarms." },
                  { n: "03", t: "Check the gasket", d: "Run a dollar bill around the door seal. If it slides out with no drag anywhere, the gasket needs replacing (~$40 part)." },
                  { n: "04", t: "Vacuum the condenser", d: "Back or bottom of the fridge, depending on model. Do this every 6 months regardless." },
                  { n: "05", t: "Wait 2 hours, then verify", d: "Recovery takes that long. Put a glass of water with an independent thermometer inside — should read 38°F or below." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Prevention */}
            <section id="prevent" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Prevention checklist
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'Leave 2" of air behind the unit — coils need to breathe',
                  "Don't overpack; 75% full is optimal",
                  "Keep thermostat at 37°F (fridge) / 0°F (freezer)",
                  "Never put warm food in — cool on counter first",
                  "Vacuum condenser coils every 6 months",
                  "Replace the door gasket every 8–10 years",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Brands */}
            <section id="brands" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                By brand · quick codes
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Brand
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Silence
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Code to watch
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        What it means
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Samsung", "Hold Freezer + Lighting 3 sec", "33 E / 26 E", "Fridge thermistor / fan fault"],
                      ["LG", "Press Alarm / Door", "Er FF / Er rS", "Freezer fan / sensor"],
                      ["GE", "Press 'Alarm'", "C or CC on display", "Temp out of range"],
                      ["Whirlpool", "Press 'Door Alarm'", "PO / PE", "Power loss / panel fault"],
                      ["Frigidaire", "Press any button 5 sec", "HI / SY EF", "High temp / evaporator fan"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[1]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[2]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </article>
        </div>
      </section>

      {/* ============ PRODUCT CAROUSEL — smart/temperature-reliable fridges ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Related gear"
          title="Fridges with better temperature control"
          description="Newer units with sealed thermistors and better defrost systems."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ SECOND CAROUSEL — different style ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-sides with through-door dispensers"
          description="Door-mounted ice and water — handy, but the #1 alarm-trigger if the door switch fails."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
